import React, { useState } from "react";
import firebase, { auth, db } from "../firebase/config";
import { useAuth } from "../context/AuthContext";
import "../styles/signInModal.css";

const DOMAIN = "@goodvibesstore.app";
const toEmail = (phone) => phone.trim().replace(/\s+/g, "") + DOMAIN;

const COUNTRY_CODES = [
  { code: "+265", flag: "🇲🇼", name: "Malawi"       },
  { code: "+267", flag: "🇧🇼", name: "Botswana"     },
  { code: "+251", flag: "🇪🇹", name: "Ethiopia"     },
  { code: "+233", flag: "🇬🇭", name: "Ghana"        },
  { code: "+254", flag: "🇰🇪", name: "Kenya"        },
  { code: "+258", flag: "🇲🇿", name: "Mozambique"   },
  { code: "+264", flag: "🇳🇦", name: "Namibia"      },
  { code: "+234", flag: "🇳🇬", name: "Nigeria"      },
  { code: "+27",  flag: "🇿🇦", name: "South Africa" },
  { code: "+255", flag: "🇹🇿", name: "Tanzania"     },
  { code: "+256", flag: "🇺🇬", name: "Uganda"       },
  { code: "+260", flag: "🇿🇲", name: "Zambia"       },
  { code: "+263", flag: "🇿🇼", name: "Zimbabwe"     },
  { code: "+44",  flag: "🇬🇧", name: "UK"           },
  { code: "+1",   flag: "🇺🇸", name: "USA / Canada" },
  { code: "+91",  flag: "🇮🇳", name: "India"        },
];

const FIREBASE_ERRORS = {
  "auth/user-not-found":      "No account with this number. Tap 'Create account' below.",
  "auth/wrong-password":      "Incorrect password.",
  "auth/email-already-in-use":"An account already exists with this number.",
  "auth/invalid-email":       "Invalid phone number format.",
  "auth/weak-password":       "Password must be at least 6 characters.",
  "auth/too-many-requests":   "Too many attempts. Try again later.",
};

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" style={{ flexShrink: 0 }}>
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0124 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 01-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
    </svg>
  );
}

export default function SignInModal({ onClose, onSuccess }) {
  const { signInWithGoogle } = useAuth();
  const [step,        setStep]        = useState("options"); // "options" | "phone"
  const [isCreating,  setIsCreating]  = useState(true);
  const [countryCode, setCountryCode] = useState("+265");
  const [localNumber, setLocalNumber] = useState("");
  const [firstName,   setFirstName]   = useState("");
  const [lastName,   setLastName]   = useState("");
  const [password,   setPassword]   = useState("");
  const [confirmPw,  setConfirmPw]  = useState("");
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState("");

  const goToPhone = () => {
    setStep("phone");
    setIsCreating(true);
    setError("");
  };

  const toggleMode = () => {
    setIsCreating((v) => !v);
    setError("");
    setPassword("");
    setConfirmPw("");
  };

  const handleLocalNumberChange = (e) => {
    // Strip leading zeros as the user types
    setLocalNumber(e.target.value.replace(/^0+/, ""));
  };

  const buildFullPhone = () => countryCode + localNumber.trim().replace(/^0+/, "");

  const handleGoogle = async () => {
    setLoading(true);
    setError("");
    try {
      await signInWithGoogle();
      onSuccess();
    } catch (e) {
      if (e.code !== "auth/popup-closed-by-user") {
        setError(FIREBASE_ERRORS[e.code] || e.message);
      }
      setLoading(false);
    }
  };

  const handlePhoneAuth = async () => {
    const fullPhone = buildFullPhone();
    if (!localNumber.trim())             { setError("Enter your phone number."); return; }
    if (!password)                       { setError("Enter your password.");     return; }
    if (isCreating && !firstName.trim()) { setError("Enter your first name.");   return; }
    if (isCreating && !lastName.trim())  { setError("Enter your last name.");    return; }
    if (isCreating && password !== confirmPw) { setError("Passwords don't match."); return; }
    if (isCreating && password.length < 6)   { setError("Password must be at least 6 characters."); return; }

    setLoading(true);
    setError("");
    try {
      if (isCreating) {
        const refCode = localStorage.getItem("gvs_ref");
        const { user } = await auth.createUserWithEmailAndPassword(toEmail(fullPhone), password);
        await user.updateProfile({ displayName: `${firstName.trim()} ${lastName.trim()}` });
        await db.collection("users").doc(user.uid).set({
          firstName: firstName.trim(),
          lastName:  lastName.trim(),
          phone:     fullPhone,
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
          ...(refCode ? { referredBy: refCode } : {}),
        });
        if (refCode) {
          const snap = await db.collection("referrals").where("code", "==", refCode).get();
          if (!snap.empty) {
            snap.docs[0].ref.update({
              totalSignups: firebase.firestore.FieldValue.increment(1),
              updatedAt:    firebase.firestore.FieldValue.serverTimestamp(),
            });
          }
          localStorage.removeItem("gvs_ref");
        }
      } else {
        await auth.signInWithEmailAndPassword(toEmail(fullPhone), password);
      }
      onSuccess();
    } catch (e) {
      setError(FIREBASE_ERRORS[e.code] || e.message);
      setLoading(false);
    }
  };

  return (
    <div className="signin-overlay" onClick={onClose}>
      <div className="signin-modal" onClick={(e) => e.stopPropagation()}>
        <button className="signin-modal__close" onClick={onClose}>✕</button>

        <div className="signin-modal__logo">GOOD <em>vibes</em> STORE</div>
        <h2 className="signin-modal__title">{isCreating && step === "phone" ? "Create account" : "Sign in"}</h2>
        <p className="signin-modal__sub">
          {step === "options" && "Continue to checkout"}
          {step === "phone" && !isCreating && "Sign in with your phone number"}
          {step === "phone" &&  isCreating && "Create your account"}
        </p>

        {step === "options" && (
          <div className="signin-modal__options">
            <button className="signin-modal__btn signin-modal__btn--google" onClick={handleGoogle} disabled={loading}>
              <GoogleIcon /> Continue with Google
            </button>
            <div className="signin-modal__divider"><span>or</span></div>
            <button className="signin-modal__btn signin-modal__btn--phone" onClick={goToPhone} disabled={loading}>
              📱 Continue with Phone
            </button>
            {error && <p className="signin-modal__error">{error}</p>}
            <p className="signin-modal__terms">
              By signing up you agree to our{" "}
              <a href="/terms" target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</a>.
            </p>
          </div>
        )}

        {step === "phone" && (
          <div className="signin-modal__form">
            <button className="signin-modal__back" onClick={() => { setStep("options"); setError(""); }}>
              ← Back
            </button>

            {isCreating && (
              <div className="signin-modal__form-row">
                <div className="signin-modal__form-group">
                  <label className="signin-modal__label">First name</label>
                  <input
                    className="signin-modal__input"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First name"
                    autoFocus
                  />
                </div>
                <div className="signin-modal__form-group">
                  <label className="signin-modal__label">Last name</label>
                  <input
                    className="signin-modal__input"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last name"
                  />
                </div>
              </div>
            )}

            <label className="signin-modal__label">Phone number</label>
            <div className="signin-modal__phone-row">
              <select
                className="signin-modal__country-select"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
              >
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.flag} {c.code} {c.name}
                  </option>
                ))}
              </select>
              <input
                className="signin-modal__input signin-modal__input--number"
                type="tel"
                value={localNumber}
                onChange={handleLocalNumberChange}
                placeholder="99 123 4567"
                autoFocus={!isCreating}
              />
            </div>

            <label className="signin-modal__label">Password</label>
            <input
              className="signin-modal__input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />

            {isCreating && (
              <>
                <label className="signin-modal__label">Confirm password</label>
                <input
                  className="signin-modal__input"
                  type="password"
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  placeholder="••••••••"
                />
              </>
            )}

            {error && <p className="signin-modal__error">{error}</p>}

            <button className="signin-modal__submit" onClick={handlePhoneAuth} disabled={loading}>
              {loading
                ? (isCreating ? "Creating account…" : "Signing in…")
                : (isCreating ? "Create account →"  : "Sign in →")}
            </button>

            {isCreating && (
              <p className="signin-modal__terms">
                By signing up you agree to our{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</a>.
              </p>
            )}

            <button className="signin-modal__toggle" onClick={toggleMode}>
              {isCreating ? "Already have an account? Sign in" : "New here? Create account"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
